/**
 * Maps technical WebSocket error messages to user-friendly messages.
 * Used to display meaningful error toasts when WebSocket errors occur.
 */

interface WebSocketErrorMapping {
  pattern: RegExp
  message: string
  isRecoverable: boolean
}

export interface MappedWebSocketError {
  message: string
  isRecoverable: boolean
}

const errorMappingList: WebSocketErrorMapping[] = [
  {
    pattern: /rate.?limit|too.?many.?requests|429/i,
    message: 'Too many requests. Please wait a moment and try again.',
    isRecoverable: true,
  },
  {
    pattern: /timeout|timed?.?out|took.?too.?long/i,
    message: 'The request took too long. Please try again.',
    isRecoverable: true,
  },
  {
    pattern: /unauthorized|unauthenticated|401|session.?expired/i,
    message: 'Your session has expired. Please refresh the page.',
    isRecoverable: false,
  },
  {
    pattern: /quota|usage.?limit|budget|exceeded/i,
    message: 'Usage limit reached. Please check your billing settings.',
    isRecoverable: false,
  },
  {
    pattern: /context.?too.?long|token.?limit|max.?tokens/i,
    message: 'The message is too long. Please try a shorter message.',
    isRecoverable: true,
  },
  {
    pattern: /model.?unavailable|model.?not.?found|service.?unavailable/i,
    message: 'The AI model is temporarily unavailable. Please try again later.',
    isRecoverable: true,
  },
  {
    pattern: /network|connection|disconnect|offline/i,
    message: 'Connection lost. Please check your internet connection.',
    isRecoverable: true,
  },
  {
    pattern: /server|internal|500|502|503|504/i,
    message: 'Something went wrong on our end. Please try again.',
    isRecoverable: true,
  },
]

const DEFAULT_ERROR: MappedWebSocketError = {
  message: 'An unexpected error occurred. Please try again.',
  isRecoverable: true,
}

export const mapWebSocketError = (errorMessage: string): MappedWebSocketError => {
  const matchedMapping = errorMappingList.find((mapping) => mapping.pattern.test(errorMessage))

  if (matchedMapping) {
    return {
      message: matchedMapping.message,
      isRecoverable: matchedMapping.isRecoverable,
    }
  }

  return DEFAULT_ERROR
}
